import { InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { FormTextFieldStyled } from '@schooly/components/form-text-field';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getPaletteColorFromName } from '../../theme/theme';
import { Spin } from '../Spin';
import { CellProps } from './cell.type';
import { ColorCellWrapper } from './CellWrappers';

const CELL_DEFAULT_WIDTH = 112;

export type PercentCellProps = Omit<CellProps<number>, 'onUpdate'> & {
  onUpdate?: (score: number | null) => Promise<void>;
};

export const PercentCell: FC<PercentCellProps> = ({ onUpdate, ...props }) => {
  const { canEdit, textColor, status, width, value, backgroundColor } = props;
  const showInput = onUpdate !== undefined && canEdit;
  const isError = status === 'error';
  const isLoading = status === 'loading';
  const colors = usePercentCellColors(status, false, textColor);
  if (!showInput) {
    return (
      <Stack
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          width,
          height: '100%',
          position: 'relative',
          backgroundColor: isError ? 'error.superLight' : undefined,
        }}
      >
        {!isLoading && <ColorCellWrapper color={backgroundColor ?? null} error={isError} />}

        <Typography sx={{ position: 'relative' }} variant="h3" color={colors.percentColor}>
          {value !== null ? `${value}%` : '-'}
        </Typography>
      </Stack>
    );
  }
  return <PercentCellEditable onUpdate={onUpdate} {...props} />;
};

const PercentCellEditable: FC<CellProps<number>> = ({
  value: initialValue,
  status,
  backgroundColor,
  onUpdate,
  width = CELL_DEFAULT_WIDTH,
  focused,
  canEdit,
  textColor,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { palette } = useTheme();
  const { isEdit, editValue, enterEditMode, exitEditMode, handleInputChange, value } = useEditMode(
    initialValue,
    focused,
    inputRef,
    onUpdate,
  );

  const isLoading = status === 'loading';
  const isError = status === 'error';
  const hasValue = value !== null;

  const colors = usePercentCellColors(status, isEdit, textColor);

  const displayValue = isEdit ? editValue : value;
  const alignCenter = !hasValue;
  const showPercentMark = !alignCenter && (value || isEdit || isError);

  const percentColorValue = getPaletteColorFromName(palette, colors.percentColor);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (canEdit) {
        enterEditMode();
      }
      if (focused) {
        e.stopPropagation();
      }
    },
    [canEdit, focused, enterEditMode],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleInputChange(event.target.value);
    },
    [handleInputChange],
  );

  return (
    <Stack
      onClick={handleClick}
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        width,
        height: '100%',
        position: 'relative',
        backgroundColor: isError ? 'error.superLight' : undefined,
      }}
    >
      {!isLoading && <ColorCellWrapper color={backgroundColor ?? null} error={isError} />}
      {isLoading && <Spin size={2} />}
      <FormTextFieldStyled
        variant="standard"
        ref={inputRef}
        onBlur={exitEditMode}
        onFocus={enterEditMode}
        onChange={handleChange}
        value={isError ? editValue : displayValue}
        placeholder={isEdit ? undefined : '-'}
        disabled={!canEdit}
        sx={({ palette, typography }) => ({
          display: isLoading ? 'none' : 'flex',

          '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
            display: 'none !important',
          },
          '& .MuiInputBase-root': {
            justifyContent: alignCenter ? 'center' : undefined,
          },
          '& .Mui-disabled': {
            color: `${percentColorValue} !important`,
            WebkitTextFillColor: `${percentColorValue} !important`,
            '& .percent-mark': {
              color: `${percentColorValue} !important`,
              WebkitTextFillColor: `${percentColorValue} !important`,
            },
            '&::placeholder': {
              color: `${palette.common.grey} !important`,
              WebkitTextFillColor: `${palette.common.grey} !important`,
            },
          },
          '& .MuiInput-input': {
            width: `calc(100% /2)`,
            textAlign: 'right',
            ...typography.h3,
            height: '44px !important',
            margin: '0 !important',
            padding: '0 !important',
            color: `${percentColorValue} !important`,
            WebkitTextFillColor: `${percentColorValue} !important`,

            '&::placeholder': {
              color: `${palette.common.grey} !important`,
              WebkitTextFillColor: `${palette.common.grey} !important`,
              opacity: 1,
            },
          },
        })}
        inputProps={{ min: 0, max: 100 }}
        InputProps={{
          sx: {
            '& input': {
              textAlign: alignCenter ? 'center !important' : 'right',
            },
          },
          endAdornment: showPercentMark ? (
            <InputAdornment className="percent-mark" position="end">
              <Stack justifyContent="flex-end">
                <Typography
                  variant="h3"
                  sx={{ textAlign: 'left', ml: -0.75 }}
                  color={colors.percentColor}
                >
                  %
                </Typography>
              </Stack>
            </InputAdornment>
          ) : null,
        }}
      />
    </Stack>
  );
};

const usePercentCellColors = (
  status: PercentCellProps['status'],
  isEdit: boolean,
  textColor?: [string, string | undefined],
) => {
  const isError = status === 'error';
  const [percentColor] = textColor || [];

  return useMemo(() => {
    if (isError) {
      return {
        percentColor: 'error.main',
        backgroundColor: 'error.superLight',
      };
    }

    if (isEdit) {
      return {
        percentColor: 'primary.main',
      };
    }

    return {
      percentColor: percentColor || 'primary.main',
    };
  }, [isError, isEdit, percentColor]);
};

const useEditMode = (
  initialValue: number | null,
  focused: boolean | undefined,
  inputRef: React.RefObject<HTMLInputElement>,
  onUpdate: (score: number | null) => Promise<void>,
) => {
  const [isEdit, setIsEdit] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const [value, setValue] = useState<string>(initialValue?.toString() ?? '');
  const initialValueRef = useRef<number | null>(null);

  const enterEditMode = useCallback(() => {
    if (focused === false) return;

    initialValueRef.current = initialValue;
    setEditValue(value?.toString() ?? '');
    setIsEdit(true);
    setTimeout(() => {
      const inputElement = inputRef.current?.querySelector('input');
      inputElement?.focus();
    }, 0);
  }, [focused, initialValue, value, inputRef]);

  const exitEditMode = useCallback(async () => {
    setIsEdit(false);

    const newValue = editValue === '' ? null : parseInt(editValue, 10);
    const hasChanged = newValue !== initialValueRef.current;

    if (hasChanged) {
      try {
        await onUpdate(newValue);
        setValue(newValue?.toString() ?? '');
      } catch (error) {
        console.error(error);
        setValue(initialValueRef.current?.toString() ?? '');
      }
    }
  }, [editValue, onUpdate]);

  const handleInputChange = useCallback((input: string) => {
    if (input === '' || /^\d*$/.test(input)) {
      setEditValue(input);
    }
  }, []);

  useEffect(() => {
    if (focused) {
      enterEditMode();
    }
  }, [focused, enterEditMode]);

  return {
    isEdit,
    editValue,
    enterEditMode,
    exitEditMode,
    handleInputChange,
    value,
  };
};
